import {computed, inject, Injectable, signal} from '@angular/core';
import {
  LicencePlanDetails,
  LicencePlanService,
  LicencePlanSummary,
  UpdateLicencePlanRequest,
} from '../../../../api';
import {lastValueFrom} from 'rxjs';
import {injectMutation, injectQuery, QueryClient} from '@tanstack/angular-query-experimental';
import {QueryKey} from '../../../constants/query-key';

@Injectable()
export class LicencePlanStoreService {
  private readonly licencePlanService = inject(LicencePlanService);
  private readonly queryClient = inject(QueryClient);

  selectedLicencePlanId = signal<number | null>(null);

  private readonly licencePlansQuery = injectQuery(() => ({
    queryKey: [QueryKey.LICENCE_PLANS],
    queryFn: () => lastValueFrom(this.licencePlanService.getLicencePlans()),
  }));

  licencePlans = computed<LicencePlanSummary[]>(() => this.licencePlansQuery.data()?.licencePlans ?? []);
  isLoading = computed(() => this.licencePlansQuery.isLoading());

  selectedLicencePlanQuery = injectQuery(() => ({
    queryKey: [QueryKey.LICENCE_PLAN, this.selectedLicencePlanId()],
    queryFn: () => lastValueFrom(this.licencePlanService.getLicencePlanById(this.selectedLicencePlanId()!)),
    enabled: this.selectedLicencePlanId() !== null,
    staleTime: 0,
  }));
  selectedLicencePlan = computed<LicencePlanDetails | null>(() => {
    if (this.selectedLicencePlanId() === null) return null;
    return this.selectedLicencePlanQuery.data()?.licencePlan ?? null;
  });
  isLoadingSelected = computed(() => this.selectedLicencePlanQuery.isLoading());

  updateLicencePlanMutation = injectMutation(() => ({
    mutationFn: (request: { id: number; body: UpdateLicencePlanRequest }) =>
      lastValueFrom(this.licencePlanService.updateLicencePlan(request.id, request.body)),
    onSuccess: (_, request) => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.LICENCE_PLANS]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.LICENCE_PLAN, request.id]});
    },
  }));
}
